/**
 * ProductsEditor — hauler tenant Products admin (DEC-175, quick 260731-e6h).
 *
 * Create / edit / deactivate the tenant product catalog. Resolves OPEN-015:
 * real hauler tenants had no way to populate `products`, so the mobile product
 * picker cached an empty list. Modeled on the Job Types editor
 * (setup-wizard/JobTypesEditor).
 *
 * Writes go to /v1/products (MANAGE_REFERENCE_DATA). Deactivate is a SOFT delete
 * (is_active=false) — job types + historical captures may reference a product.
 * `code` is immutable after creation (the stable machine key). Unit-of-measure
 * is a fixed per-product property (BBL, EACH, TOTE, LOAD, …); the dropdown
 * suggests common values but any A–Z/0–9/underscore token is accepted.
 *
 * Out of scope (deliberate): per-unit rate. PER_ITEM billing is a separate scope
 * (ClickUp 86bb4e23c) — a product's rate stays NULL and is not edited here.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { fieldStyle, FormRow } from '../sites-operators/form-bits';
import { PRODUCTS_QUERY_KEY } from '../jobs/use-products';
import {
  useProductsAdmin,
  PRODUCTS_ADMIN_QUERY_KEY,
  COMMON_UNITS,
  type AdminProduct,
} from './use-products-admin';
import { formatNumber } from '@lodex-pro/shared/number-format';

const subText: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid var(--color-border-primary)',
  marginBottom: '6px',
};
const btn: React.CSSProperties = {
  fontSize: '13px',
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-primary)',
  background: 'transparent',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  ...btn,
  background: 'var(--color-accent-blue)',
  borderColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const upper: React.CSSProperties = { textTransform: 'uppercase' };

interface FormState {
  name: string;
  code: string;
  unitOfMeasure: string;
}
const EMPTY_FORM: FormState = { name: '', code: '', unitOfMeasure: 'BBL' };

const CODE_RE = /^[A-Z0-9_]{2,40}$/;
const UNIT_RE = /^[A-Z0-9_]{1,16}$/;

export function ProductsEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const { data, isLoading } = useProductsAdmin(opts);
  const products = data?.products ?? [];

  const [creating, setCreating] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const invalidate = React.useCallback(async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: PRODUCTS_ADMIN_QUERY_KEY }),
      // Refresh the read-only picker query too (job-type wizard step 1, mobile
      // parity) so a newly created / renamed product shows immediately.
      qc.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY }),
    ]);
  }, [qc]);

  const startCreate = (): void => {
    setForm(EMPTY_FORM);
    setCreating(true);
    setEditingId(null);
    setError(null);
  };
  const startEdit = (p: AdminProduct): void => {
    setForm({ name: p.name, code: p.code, unitOfMeasure: p.unitOfMeasure });
    setEditingId(p.id);
    setCreating(false);
    setError(null);
  };
  const cancel = (): void => {
    setCreating(false);
    setEditingId(null);
    setError(null);
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = async (): Promise<void> => {
    const name = form.name.trim();
    const code = form.code.trim().toUpperCase();
    const unit = form.unitOfMeasure.trim().toUpperCase();
    if (name === '') {
      setError('Name is required.');
      return;
    }
    if (!UNIT_RE.test(unit)) {
      setError('Unit is required (1–16 chars: A–Z, 0–9, underscore).');
      return;
    }
    // `code` is only validated / sent on create — it is immutable on edit.
    if (editingId === null && !CODE_RE.test(code)) {
      setError('Code must be 2–40 chars: A–Z, 0–9, underscore.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const isEdit = editingId !== null;
      const path = isEdit ? `/v1/products/${editingId}` : '/v1/products';
      const body = isEdit ? { name, unitOfMeasure: unit } : { name, code, unitOfMeasure: unit };
      const res = await authedFetch(opts, path, {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setError(
          res.status === 400
            ? 'Check your inputs — a product with this code may already exist.'
            : `Save failed (${String(res.status)}).`,
        );
        return;
      }
      await invalidate();
      toast.success(isEdit ? 'Product saved' : 'Product created');
      cancel();
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (p: AdminProduct, isActive: boolean): Promise<void> => {
    try {
      const res = await authedFetch(opts, `/v1/products/${p.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      });
      if (!res.ok) {
        toast.error('Could not update. Please try again.');
        return;
      }
      await invalidate();
      toast.success(isActive ? 'Product reactivated' : 'Product deactivated');
    } catch {
      toast.error('Network error — please try again.');
    }
  };

  const activeCount = products.filter((p) => p.isActive).length;
  const formOpen = creating || editingId !== null;

  return (
    <div style={{ maxWidth: '560px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={subText}>
          {formatNumber(activeCount)} active product{activeCount === 1 ? '' : 's'}
        </div>
        {!formOpen && (
          <button type="button" style={primaryBtn} onClick={startCreate}>
            + New product
          </button>
        )}
      </div>

      {formOpen && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            padding: '12px',
            marginTop: '10px',
            borderRadius: 'var(--radius-card)',
            background: 'var(--color-bg-secondary)',
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {editingId !== null ? 'Edit product' : 'New product'}
          </div>

          <FormRow label="Name" htmlFor="prod-name" required>
            <input
              id="prod-name"
              value={form.name}
              placeholder="Produced Water"
              onChange={(e): void => set('name', e.target.value)}
              style={fieldStyle}
            />
          </FormRow>

          <FormRow label="Code" htmlFor="prod-code" required>
            <>
              <input
                id="prod-code"
                value={form.code}
                disabled={editingId !== null}
                placeholder="PRODUCED_WATER"
                onChange={(e): void => set('code', e.target.value)}
                style={{ ...fieldStyle, ...upper }}
              />
              <div style={subText}>
                {editingId !== null
                  ? 'Code is fixed after creation.'
                  : 'Stable machine identifier — 2–40 chars: A–Z, 0–9, underscore.'}
              </div>
            </>
          </FormRow>

          <FormRow label="Unit" htmlFor="prod-unit" required>
            <>
              <input
                id="prod-unit"
                list="prod-unit-options"
                value={form.unitOfMeasure}
                placeholder="BBL"
                onChange={(e): void => set('unitOfMeasure', e.target.value)}
                style={{ ...fieldStyle, ...upper }}
              />
              <datalist id="prod-unit-options">
                {COMMON_UNITS.map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
              <div style={subText}>
                How this product is measured when billed (e.g. BBL, EACH, TOTE).
              </div>
            </>
          </FormRow>

          {error !== null && (
            <div style={{ fontSize: '13px', color: 'var(--color-danger)' }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button
              type="button"
              style={primaryBtn}
              disabled={saving}
              onClick={(): void => void save()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" style={btn} disabled={saving} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: '12px' }}>
        {isLoading && <div style={subText}>Loading…</div>}
        {!isLoading && products.length === 0 && (
          <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontWeight: 600 }}>No products yet</span>
            <span style={subText}>
              Add the products your drivers capture (e.g. Produced Water, Fresh Water). Drivers pick
              from these on the run-ticket screen.
            </span>
          </div>
        )}
        {products.map((p) => (
          <div key={p.id} style={{ ...rowStyle, opacity: p.isActive ? 1 : 0.55 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{p.name}</span>{' '}
              <span style={subText}>
                · {p.code} · {p.unitOfMeasure}
              </span>
              {!p.isActive && <span style={subText}> · inactive</span>}
            </div>
            <button type="button" style={btn} onClick={(): void => startEdit(p)}>
              Edit
            </button>
            <button type="button" style={btn} onClick={(): void => void setActive(p, !p.isActive)}>
              {p.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
